import {
  RequestWorkflow,
  WORKFLOW_FORM_FIELD_TYPES,
  WorkflowFormSubmission,
  WorkflowFormSubmissionFile,
  WorkflowFormValidationError,
  WorkflowNodeForm,
  WorkflowSubmissionRequestFile,
} from '@workflows/types';
import { RequestDTO } from '@core/types';

export const SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS = {
  validationErrors: 'validationErrors',
  alreadyCompleted: 'alreadyCompleted',
  logisticRequestNotFound: 'logisticRequestNotFound',
  formNotFound: 'formNotFound',
  requestWorkflowNotFound: 'requestWorkflowNotFound',
  badNextFormId: 'badNextFormId',
  workflowRequestMismatch: 'workflowRequestMismatch',
  duplicateSubmissions: 'duplicateSubmissions',
  formHasNoField: 'formHasNoField',
  invalidFieldType: 'invalidFieldType',
  submissionFileNotFound: 'submissionFileNotFound',
  submissionFileMismatch: 'submissionFileMismatch',
  fileCategoryNotFound: 'fileCategoryNotFound',
  fileMissing: 'fileMissing',
  invalidFileMimetype: 'invalidFileMimetype',
  fileTooLarge: 'fileTooLarge',
  tooManyFiles: 'tooManyFiles',
} as const;

export type SyncWorkflowResponseSuccess = {
  isOk: true;
};

export type SyncWorkflowResponseUploadFileSuccess =
  SyncWorkflowResponseSuccess & {
    data: {
      createdSubmissionFileId: WorkflowFormSubmissionFile['id'];
      uploadedToSubmissionId: WorkflowFormSubmission['id'];
      /** Загруженный файл заявки (backend SYST-3008); у старого backend отсутствует */
      uploadedFile?: WorkflowSubmissionRequestFile;
    };
  };

/** Нарушение ограничений файла поля: MIME, размер, количество */
export type SyncWorkflowResponseFileLimitViolated = {
  isOk: false;
  failureReason:
    | typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.fileMissing
    | typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.invalidFileMimetype
    | typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.fileTooLarge
    | typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.tooManyFiles;
  context: {
    fieldKey: string;
    mimetype?: string;
    allowedFilesMimetypes?: string[];
    maxFileSizeBytes?: number;
    maxFilesCount?: number;
  };
};

export type SyncWorkflowResponseRemoveFileSuccess =
  SyncWorkflowResponseSuccess & {
    data: {
      syncedSubmissionId: WorkflowFormSubmission['id'];
    };
  };

export type SyncWorkflowFormSuccessResponse = SyncWorkflowResponseSuccess & {
  data: {
    syncedSubmissionId: WorkflowFormSubmission['id'];
    conflictingSubmissionIds?: WorkflowFormSubmission['id'][]; // Если у формы есть несколько сабмишенов
  };
};

export type SyncWorkflowResponseValidationErrors = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.validationErrors;
  context: {
    syncedSubmissionId: WorkflowFormSubmission['id'];
    validationErrors?: WorkflowFormValidationError[];
  };
};

export type SyncWorkflowResponseSubmitValidationErrors = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.validationErrors;
  context: {
    validationErrors?: WorkflowFormValidationError[];
  };
};

export type SyncWorkflowResponseAlreadyCompleted = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.alreadyCompleted;
};

export type SyncWorkflowResponseLogisticRequestNotFound = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.logisticRequestNotFound;
  context: {
    requestNumber: RequestDTO['requestNumber'];
  };
};

export type SyncWorkflowResponseFormNotFound = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.formNotFound;
  context: {
    formId: WorkflowNodeForm['id'];
  };
};

export type SyncWorkflowResponseRequestWorkflowNotFound = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.requestWorkflowNotFound;
  context:
    | {
        requestNumber: RequestDTO['requestNumber'];
        formId: WorkflowNodeForm['id'];
      }
    | {
        requestWorkflowId: RequestWorkflow['id'];
      };
};

export type SyncWorkflowResponseBadNextFormId = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.badNextFormId;
  context: {
    nextFormId: WorkflowNodeForm['id'];
  };
};

export type SyncWorkflowResponseWorkflowRequestMismatch = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.workflowRequestMismatch;
  context: {
    requestNumber: RequestDTO['requestNumber'];
    requestWorkflowId: RequestWorkflow['id'];
  };
};

export type SyncWorkflowResponseDuplicateSubmissions = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.duplicateSubmissions;
  context: {
    submissionsIds: WorkflowFormSubmission['id'][];
    formId: WorkflowNodeForm['id'];
  };
};

export type SyncWorkflowResponseFormHasNoField = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.formHasNoField;
  context: {
    formId: WorkflowNodeForm['id'];
    fieldKey: string;
  };
};

export type SyncWorkflowResponseInvalidFieldType = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.invalidFieldType;
  context: {
    formId: WorkflowNodeForm['id'];
    fieldKey: string;
    fieldType: keyof typeof WORKFLOW_FORM_FIELD_TYPES;
  };
};

export type SyncWorkflowResponseSubmissionFileNotFound = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.submissionFileNotFound;
  context: {
    submissionFileId: number;
  };
};

export type SyncWorkflowResponseSubmissionFileMismatch = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.submissionFileMismatch;
  context: {
    submissionFileId: number;
    requestNumber: RequestDTO['requestNumber'];
  };
};

export type SyncWorkflowResponseFileCategoryNotFound = {
  isOk: false;
  failureReason: typeof SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.fileCategoryNotFound;
  context: {
    fileCategoryId: number;
  };
};

export type SyncWorkflowFormResponse =
  | SyncWorkflowFormSuccessResponse
  | SyncWorkflowResponseValidationErrors
  | SyncWorkflowResponseAlreadyCompleted
  | SyncWorkflowResponseLogisticRequestNotFound
  | SyncWorkflowResponseFormNotFound
  | SyncWorkflowResponseRequestWorkflowNotFound
  | SyncWorkflowResponseBadNextFormId;

export type SyncWorkflowFormErrorResponse = Exclude<
  SyncWorkflowFormResponse,
  SyncWorkflowFormSuccessResponse
>;

export type SyncWorkflowUploadFileResponse =
  | SyncWorkflowResponseUploadFileSuccess
  | SyncWorkflowResponseLogisticRequestNotFound
  | SyncWorkflowResponseFormNotFound
  | SyncWorkflowResponseRequestWorkflowNotFound
  | SyncWorkflowResponseAlreadyCompleted
  | SyncWorkflowResponseFormHasNoField
  | SyncWorkflowResponseInvalidFieldType
  | SyncWorkflowResponseFileCategoryNotFound
  | SyncWorkflowResponseFileLimitViolated;

export type SyncWorkflowRemoveFileResponse =
  | SyncWorkflowResponseRemoveFileSuccess
  | SyncWorkflowResponseLogisticRequestNotFound
  | SyncWorkflowResponseFormNotFound
  | SyncWorkflowResponseRequestWorkflowNotFound
  | SyncWorkflowResponseSubmissionFileNotFound
  | SyncWorkflowResponseSubmissionFileMismatch
  | SyncWorkflowResponseAlreadyCompleted;

export type SyncWorkflowSubmitResponse =
  | SyncWorkflowResponseSuccess
  | SyncWorkflowResponseAlreadyCompleted
  | SyncWorkflowResponseLogisticRequestNotFound
  | SyncWorkflowResponseRequestWorkflowNotFound
  | SyncWorkflowResponseWorkflowRequestMismatch
  | SyncWorkflowResponseDuplicateSubmissions
  | SyncWorkflowResponseSubmitValidationErrors;
